/**
 * Survie au rechargement de page hors ligne.
 *
 * PROBLÈME RÉSOLU. Le cache de données est en mémoire seule (choix RGPD). Au
 * rechargement, `GET /auth/me/` est rejoué : hors ligne il échoue, l'AuthGuard
 * renvoie vers `/login`… qui exige le réseau. Un simple F5 pendant une coupure
 * déconnectait donc l'utilisateur et emportait sa file d'écritures.
 *
 * Le cookie JWT (httpOnly) survit, lui, au rechargement : la session est
 * toujours valide côté serveur. Il ne manque que de quoi redémarrer l'interface
 * sans réseau.
 *
 * CE QUI EST CONSERVÉ, et rien d'autre :
 *   - la réponse `/auth/me/` : profil de l'AGENT connecté (pas un patient),
 *     ses rôles et la configuration d'affichage de sa clinique ;
 *   - les écritures en file : créations saisies hors ligne, pas encore
 *     synchronisées. Elles contiennent des données patient — c'est assumé :
 *     les perdre au moindre rechargement est un risque clinique plus grave que
 *     leur présence temporaire dans le stockage de l'onglet.
 *
 * OÙ : `sessionStorage`, pas `localStorage`. La portée est l'onglet : tout est
 * effacé à sa fermeture, donc rien ne subsiste pour l'agent suivant sur un
 * poste partagé. Purge explicite en plus à la déconnexion et dès que la file
 * se vide.
 *
 * AUCUNE liste de patients, aucune consultation, aucune vente n'est écrite ici.
 */
import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";

const STORAGE_KEY = "mada.offline-session.v1";

/** `sessionStorage` peut lever (mode privé, quota) : jamais bloquant. */
function safeStorage(): Storage | null {
  try {
    const probe = "__mada_probe__";
    window.sessionStorage.setItem(probe, "1");
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Seule la requête `["auth","me"]` est conservée — jamais les données métier. */
function isSessionQuery(queryKey: readonly unknown[]): boolean {
  return queryKey[0] === "auth" && queryKey[1] === "me";
}

/** Écrit l'instantané de session. Sans écriture en file ni `me`, purge à la place. */
export function saveOfflineSession(qc: QueryClient): void {
  const storage = safeStorage();
  if (!storage) return;

  const state = dehydrate(qc, {
    shouldDehydrateQuery: (query) =>
      isSessionQuery(query.queryKey) && query.state.status === "success",
    // Uniquement les écritures en attente : une mutation déjà partie n'a rien
    // à faire ici (elle serait rejouée en double au rechargement).
    shouldDehydrateMutation: (mutation) => mutation.state.isPaused,
  });

  if (state.queries.length === 0 && state.mutations.length === 0) {
    storage.removeItem(STORAGE_KEY);
    return;
  }

  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Quota dépassé : on préfère une session non restaurable à une exception
    // qui casserait la saisie en cours.
  }
}

/**
 * Restaure l'instantané dans le client. À appeler AVANT le premier rendu, pour
 * que l'AuthGuard trouve `me` en cache au lieu de repartir vers la connexion.
 * Renvoie `true` si quelque chose a été restauré.
 */
export function restoreOfflineSession(qc: QueryClient): boolean {
  const storage = safeStorage();
  if (!storage) return false;

  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return false;

  try {
    hydrate(qc, JSON.parse(raw));
    return true;
  } catch {
    // Instantané illisible (format changé, écriture partielle) : on repart
    // proprement plutôt que de traîner un état incohérent.
    storage.removeItem(STORAGE_KEY);
    return false;
  }
}

/** Purge — déconnexion, ou session devenue invalide côté serveur. */
export function clearOfflineSession(): void {
  safeStorage()?.removeItem(STORAGE_KEY);
}

/**
 * Maintient l'instantané à jour : à chaque évolution de la file d'écritures ou
 * de `/auth/me/`. Renvoie la fonction de désabonnement.
 */
export function watchOfflineSession(qc: QueryClient): () => void {
  const persist = () => saveOfflineSession(qc);

  const unsubscribeMutations = qc.getMutationCache().subscribe(persist);
  const unsubscribeQueries = qc.getQueryCache().subscribe((event) => {
    if (isSessionQuery(event.query.queryKey)) persist();
  });
  // Filet : un onglet fermé brutalement pendant une saisie doit quand même
  // laisser le dernier état connu.
  window.addEventListener("pagehide", persist);

  persist();

  return () => {
    unsubscribeMutations();
    unsubscribeQueries();
    window.removeEventListener("pagehide", persist);
  };
}
